import sharePointSubmitAdapter from './sharePointSubmitAdapter';
import shpListAdapter from '../../dataConnections/sharePointList/sharePointListAdapter';
import xmlFileAdapter from './xmlFileAdapter';
import udcxFileAdapter from './udcxFileAdapter';
import adoAdapter from './adoAdapter';
import soapAdapter from './soapAdapter';
import {
    CONNECTION_TYPE_ADO,
    CONNECTION_TYPE_DAV,
    CONNECTION_TYPE_SHPLIST,
    CONNECTION_TYPE_SHPLISTFORM,
    CONNECTION_TYPE_UDCX,
    CONNECTION_TYPE_WEBSERVICE,
    CONNECTION_TYPE_XMLFILE,
    elementNameToType,
} from './dataConnection.types';
import sharePointListFormAdapter from '../../dataConnections/sharePointListForm/sharePointListFormAdapter';

const queryConnectionTypes = [CONNECTION_TYPE_XMLFILE, CONNECTION_TYPE_SHPLIST];

function dataConnectionFactory(api, shpAccess, template, xpathEngine, dataSources, settings) {

    function dataSourceName(definition) {
        return definition.isMainQuery ? "" : definition.name;
    }

    function getAdapter(definition) {
        const adapterDef = definition;

        switch (adapterDef.type) {
            case CONNECTION_TYPE_XMLFILE:
                return xmlFileAdapter(adapterDef, api, shpAccess, template);
            case CONNECTION_TYPE_SHPLIST:
                return shpListAdapter(adapterDef, template, shpAccess, settings);
            case CONNECTION_TYPE_WEBSERVICE:
                return soapAdapter(adapterDef, template, api, xpathEngine, dataSources);
            case CONNECTION_TYPE_DAV:
                return sharePointSubmitAdapter(
                    adapterDef,
                    template,
                    shpAccess,
                    dataSources,
                );
            case CONNECTION_TYPE_UDCX:
                return udcxFileAdapter(adapterDef, getAdapter, template, shpAccess, api);
            case CONNECTION_TYPE_ADO:
                return adoAdapter(adapterDef, template, xpathEngine, dataSources, dataSourceName(definition), api);
            case CONNECTION_TYPE_SHPLISTFORM:
                return sharePointListFormAdapter(
                    adapterDef,
                    template,
                    dataSources,
                    shpAccess,
                    settings,
                );
        }

        console.warn("Unknown adapter type: " + definition.type);
        return null;
    }

    function isQueryAdapter(definition) {
        var type = definition.type;

        return queryConnectionTypes.indexOf(type) !== -1 ||
            (type === CONNECTION_TYPE_WEBSERVICE && definition.queryAllowed);
    }

    function makeDataConnection(definition) {
        const { name, type } = definition;
        const adapter = getAdapter(definition);
        const isQuery = isQueryAdapter(definition);
        const dataSource = dataSources.getDataSource(dataSourceName(definition));

        function setUrl(queryUrl) {
            if (adapter.setUrl) {
                return adapter.setUrl(queryUrl);
            }

            throw new Error("Adapter " + name + ' does not support changing its url.');
        }

        function setUrlAndListInfo(siteUrl, listId, listName) {
            if (adapter.setUrlAndListInfo) {
                return adapter.setUrlAndListInfo(siteUrl, listId, listName);
            }
        }

        async function initAsync() {
            if (!adapter.initAsync) {
                return;
            }

            await adapter.initAsync(dataSource);

            if (dataSource) {
                dataSource.reassignIds();
            }
        }

        async function queryAsync(...args) {
            const allArguments = [dataSource, ...args];

            await (adapter.executeQueryAsync
                ? adapter.executeQueryAsync(...allArguments)
                : adapter.executeAsync(...allArguments)
            );

            dataSource.reassignIds();
        }

        const submitAsync = async (...args) => await adapter.executeAsync(...args);

        const executeAsync = async (...args) =>
            isQuery
                ? await queryAsync(...args)
                : await submitAsync(...args);

        function getUrl() {
            if (adapter.getUrl) {
                return adapter.getUrl();
            }

            throw new Error("Adapter " + name + ' does not support getting its url.');
        }

        function getCommandText() {
            if (adapter.getCommandText) {
                return adapter.getCommandText();
            }

            throw new Error("Adapter " + name + ' does not support getting its command text.');
        }

        function setCommandText(commandString) {
            if (adapter.setCommandText) {
                return adapter.setCommandText(commandString);
            }

            throw new Error("Adapter " + name + ' does not support setting its command text.');
        }

        function isAdoSupported() {
            return !!adapter.isAdoSupported;
        }

        function queryByIdAsync(listItemId) {
            if (!adapter.queryByIdAsync) {
                throw new Error("Adapter " + name + ' does not support querying by id.');
            }

            return adapter.queryByIdAsync(dataSource, listItemId)
                .then(function () {
                    dataSource.reassignIds();
                });
        }

        if (!adapter) {
            console.warn('Failed to create data connection. Could not create adapter.');
            return null;
        }

        const getExternalType = () =>
            type === CONNECTION_TYPE_UDCX
                ? elementNameToType(definition.localName)
                : type;

        return {
            name,
            initAsync,
            executeAsync,
            queryAsync,
            submitAsync,
            dataSource,
            setUrl,
            setUrlAndListInfo,
            type: getExternalType(),
            getUrl,
            getCommandText,
            setCommandText,
            isAdoSupported,
            queryByIdAsync,
            isMainQuery: !!definition.isMainQuery,
            definition,
        };
    }

    // interface
    return {
        makeDataConnection,
    };
}


export default dataConnectionFactory;